/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";

// Icons
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import PaymentOutlinedIcon from "@mui/icons-material/PaymentOutlined";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";

import Loader from "../../loader/Loader";
import { formatDateTimeForAdmin } from "../../../utils/dateUtils";
import "./styles.scss";

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={0} ref={ref} variant="filled" {...props} />;
});

// Format Indian Currency
const formatMoney = (amount) => {
  const val = Number(amount) || 0;
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(val);
};

const getOrderItemImage = (item = {}) => {
  const candidates = [
    item.imageUrl,
    item.image,
    Array.isArray(item.images) ? item.images[0] : null,
    item.product?.imageUrl,
    item.product?.image,
    Array.isArray(item.product?.images) ? item.product.images[0] : null,
  ];

  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate) return candidate;
    if (candidate && typeof candidate === "object") {
      const url = candidate.url || candidate.src || candidate.downloadURL;
      if (url) return url;
    }
  }

  return "";
};

// Status Badge Config matching back-office orders theme
const getStatusBadge = (statusRaw = "") => {
  const s = statusRaw.toLowerCase();
  if (s.includes("delivered")) {
    return { label: "Delivered", bg: "#ecfdf5", color: "#047857", border: "#a7f3d0" };
  }
  if (s.includes("shipped")) {
    return { label: "Shipped", bg: "#f5f3ff", color: "#6d28d9", border: "#ddd6fe" };
  }
  if (s.includes("out for delivery")) {
    return { label: "Out for Delivery", bg: "#eef2ff", color: "#4338ca", border: "#c7d2fe" };
  }
  if (s.includes("placed") || s.includes("pending")) {
    return { label: statusRaw || "Placed", bg: "#fffbeb", color: "#b45309", border: "#fde68a" };
  }
  if (s.includes("cancel")) {
    return { label: statusRaw || "Cancelled", bg: "#fef2f2", color: "#b91c1c", border: "#fecaca" };
  }
  if (s.includes("refund")) {
    return { label: "Refunded", bg: "#eff6ff", color: "#1d4ed8", border: "#bfdbfe" };
  }
  return { label: statusRaw || "Placed", bg: "#fffbeb", color: "#b45309", border: "#fde68a" };
};

// Payment Status Badge
const getPaymentBadge = (statusRaw = "") => {
  const s = (statusRaw || "").toLowerCase();
  if (s.includes("captured") || s.includes("success") || s.includes("paid")) {
    return { label: statusRaw || "Paid", bg: "#ecfdf5", color: "#047857", border: "#a7f3d0" };
  }
  if (s.includes("authorized")) {
    return { label: statusRaw || "Authorized", bg: "#eff6ff", color: "#1d4ed8", border: "#bfdbfe" };
  }
  if (s.includes("failed") || s.includes("error")) {
    return { label: statusRaw || "Failed", bg: "#fef2f2", color: "#b91c1c", border: "#fecaca" };
  }
  return { label: statusRaw || "Pending", bg: "#fffbeb", color: "#b45309", border: "#fde68a" };
};

const fulfillmentSteps = [
  { value: "Placed", label: "Placed" },
  { value: "Shipped", label: "Shipped" },
  { value: "Out for delivery", label: "Out for delivery" },
  { value: "Delivered", label: "Delivered" },
];

const isCreatedOrder = (status) => String(status || "").trim().toUpperCase() === "CREATED";

const ViewOrder = ({ detailedOrder, hideModal, onSetOrder, onDeleteOrder }) => {
  const [initOrder, setInitOrder] = useState({});
  const [loading, setLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [formError, setFormError] = useState("");
  const [copiedKey, setCopiedKey] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (detailedOrder && detailedOrder.id) {
      setInitOrder({
        ...detailedOrder,
        courierDetails: detailedOrder.courierDetails || {
          partner: "",
          trackingId: "",
          trackingUrl: "",
        },
      });
      setLoading(false);
    }
  }, [detailedOrder]);

  const handleCopy = (text, key) => {
    if (!text) return;
    navigator.clipboard?.writeText?.(String(text));
    setCopiedKey(key);
    setTimeout(() => {
      setCopiedKey("");
    }, 1800);
  };

  const handleChangeOrderDetail = (updateData) => {
    setInitOrder((prev) => ({
      ...prev,
      ...updateData,
    }));
    if (formError) setFormError("");
  };

  const handleCourierChange = (field, value) => {
    setInitOrder((prev) => ({
      ...prev,
      courierDetails: {
        ...(prev.courierDetails || {}),
        [field]: value,
      },
    }));
    if (formError) setFormError("");
  };

  const onClickUpdate = async () => {
    const courier = initOrder.courierDetails || {};
    if (initOrder.status === "Shipped") {
      if (!courier.partner || courier.partner.trim().length < 2) {
        setFormError("Please enter a valid Courier Partner name.");
        return;
      }
      if (!courier.trackingId || courier.trackingId.trim().length < 2) {
        setFormError("Please enter the Tracking ID.");
        return;
      }
    }

    let dataToSend = { ...initOrder };

    if (initOrder.status === "Shipped" && !dataToSend.shippedAt) {
      dataToSend.shippedAt = new Date();
    }
    if (initOrder.status === "Delivered" && !dataToSend.deliveredAt) {
      dataToSend.deliveredAt = new Date();
    }
    if (initOrder.status === "Out for delivery" && !dataToSend.ofdAt) {
      dataToSend.ofdAt = new Date();
    }
    if (
      (initOrder.status === "Cancelled by buyer" ||
        initOrder.status === "Cancelled by seller") &&
      !dataToSend.cancelledAt
    ) {
      dataToSend.cancelledAt = new Date();
    }
    if (initOrder.status === "Refunded" && !dataToSend.refundedAt) {
      dataToSend.refundedAt = new Date();
    }

    setIsUpdating(true);
    try {
      await onSetOrder(dataToSend);
      hideModal();
    } catch (err) {
      console.error("Error updating order:", err);
      setFormError("Failed to update order status. Please try again.");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteOrder = async () => {
    if (!isCreatedOrder(initOrder.status) || !onDeleteOrder) return;

    setIsDeleting(true);
    setFormError("");
    try {
      await onDeleteOrder(initOrder.id);
      setDeleteDialogOpen(false);
      hideModal();
    } catch (err) {
      console.error("Error deleting order:", err);
      setFormError("Failed to delete this draft order. Please try again.");
      setDeleteDialogOpen(false);
    } finally {
      setIsDeleting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 6, display: "flex", justifyContent: "center", alignItems: "center" }}>
        <Loader type="CIRCULAR" />
      </Box>
    );
  }

  const statusBadge = getStatusBadge(initOrder.status);
  const paymentBadge = getPaymentBadge(initOrder.payment?.status);
  const items = Array.isArray(initOrder.items) ? initOrder.items : [];
  const itemsSubtotal = items.reduce(
    (sum, item) => sum + (Number(item.sellingPrice || item.listPrice || 0) * Number(item.quantity || 1)),
    0
  );
  const grandTotal = Number(initOrder.amount || itemsSubtotal);
  const couponCode = initOrder.couponApplied?.couponCode || initOrder.couponApplied?.code || "APPLIED";
  const couponDiscount = Number(
    initOrder.couponApplied?.amount ??
    initOrder.couponApplied?.discount ??
    initOrder.couponApplied?.discountAmount ??
    0
  );
  const activeStep = fulfillmentSteps.findIndex((step) => step.value === initOrder.status);
  const isExceptionStatus = ["Cancelled by buyer", "Cancelled by seller", "Refunded"].includes(initOrder.status);
  const originalCourier = detailedOrder?.courierDetails || {};
  const currentCourier = initOrder.courierDetails || {};
  const hasChanges =
    initOrder.status !== detailedOrder?.status ||
    ["partner", "trackingId", "trackingUrl"].some(
      (field) => (currentCourier[field] || "") !== (originalCourier[field] || "")
    );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        maxHeight: "90vh",
        bgcolor: "#ffffff",
      }}
    >
      {/* 1. Modal Top Bar / Header */}
      <Box
        sx={{
          px: { xs: 2.5, sm: 3.5 },
          py: 2.5,
          borderBottom: "1px solid #f1f5f9",
          bgcolor: "#ffffff",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexShrink: 0,
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: "12px",
              bgcolor: "rgba(0, 77, 64, 0.08)",
              color: "#004d40",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <ReceiptLongOutlinedIcon />
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 800,
                  color: "#1e293b",
                  fontSize: { xs: "1.05rem", sm: "1.2rem" },
                  letterSpacing: "-0.01em",
                  maxWidth: { xs: 190, sm: 420 },
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title={`Order #${initOrder.id}`}
              >
                Order #{initOrder.id}
              </Typography>

              <Tooltip title={copiedKey === "orderId" ? "Copied!" : "Copy Order ID"}>
                <IconButton
                  size="small"
                  onClick={() => handleCopy(initOrder.id, "orderId")}
                  aria-label="Copy order ID"
                  sx={{
                    color: "#94a3b8",
                    "&:hover": { color: "#004d40", bgcolor: "rgba(0,77,64,0.06)" },
                  }}
                >
                  <ContentCopyIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>

              <Chip
                label={isCreatedOrder(initOrder.status) ? "Payment Pending" : statusBadge.label}
                size="small"
                sx={{
                  fontWeight: 800,
                  fontSize: "0.75rem",
                  bgcolor: statusBadge.bg,
                  color: statusBadge.color,
                  border: `1px solid ${statusBadge.border}`,
                  height: 24,
                }}
              />
            </Stack>

            <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 500, display: "block", mt: 0.3 }}>
              Placed on {formatDateTimeForAdmin(initOrder.createdAt)} • {items.length} {items.length === 1 ? "item" : "items"} • Total:{" "}
              <strong style={{ color: "#004d40" }}>{formatMoney(grandTotal)}</strong>
            </Typography>
          </Box>
        </Stack>

        <IconButton
          onClick={hideModal}
          size="small"
          aria-label="Close order details"
          sx={{
            color: "#64748b",
            bgcolor: "#f8fafc",
            "&:hover": { bgcolor: "#f1f5f9", color: "#0f172a" },
            flexShrink: 0,
            ml: 1,
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* 2. Scrollable Body Content */}
      <Box sx={{ p: { xs: 2.5, sm: 3.5 }, overflowY: "auto", flex: 1, bgcolor: "#f8fafc" }}>
        <Stack spacing={2.5}>
          {/* Validation Alert */}
          {formError && (
            <Alert
              severity="error"
              icon={<WarningAmberRoundedIcon />}
              onClose={() => setFormError("")}
              sx={{
                borderRadius: "8px",
                bgcolor: "#fef2f2",
                color: "#991b1b",
                border: "1px solid #fecaca",
                "& .MuiAlert-icon": { color: "#dc2626" },
              }}
            >
              {formError}
            </Alert>
          )}

          {/* SECTION A: Status & Fulfillment Card */}
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: "8px",
              border: "1px solid #e2e8f0",
              bgcolor: "#ffffff",
              boxShadow: "0 2px 10px rgba(0,0,0,0.02)",
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1.5} sx={{ mb: 2 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <LocalShippingOutlinedIcon sx={{ color: "#004d40", fontSize: 20 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#0f172a" }}>
                  Order Status & Fulfillment
                </Typography>
              </Stack>
              <Chip
                label={statusBadge.label}
                size="small"
                sx={{ bgcolor: statusBadge.bg, color: statusBadge.color, border: `1px solid ${statusBadge.border}`, fontWeight: 800 }}
              />
            </Stack>

            <Box
              aria-label="Order fulfillment progress"
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm: "repeat(4, minmax(0, 1fr))" },
                gap: { xs: 1, sm: 0 },
                mb: 2.5,
                p: 1.5,
                border: `1px solid ${isExceptionStatus ? statusBadge.border : "#dce9e4"}`,
                borderRadius: "8px",
                bgcolor: isExceptionStatus ? statusBadge.bg : "#f7fbf9",
              }}
            >
              {isExceptionStatus ? (
                <Box sx={{ gridColumn: "1 / -1", display: "flex", alignItems: "center", gap: 1 }}>
                  <WarningAmberRoundedIcon sx={{ color: statusBadge.color, fontSize: 20 }} />
                  <Box>
                    <Typography variant="caption" sx={{ display: "block", color: statusBadge.color, fontWeight: 800 }}>
                      Fulfillment stopped: {statusBadge.label}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#64748b" }}>
                      Select a different status below to resume order processing.
                    </Typography>
                  </Box>
                </Box>
              ) : fulfillmentSteps.map((step, index) => {
                const completed = activeStep >= index;
                const current = activeStep === index;
                return (
                  <Box key={step.value} sx={{ position: "relative", display: "flex", alignItems: "center", gap: 1, minWidth: 0, "&::after": index < fulfillmentSteps.length - 1 ? { content: '""', position: "absolute", left: 28, right: 0, top: 12, height: 2, bgcolor: activeStep > index ? "#36a37b" : "#d7e4df", display: { xs: "none", sm: "block" } } : undefined }}>
                    <Box sx={{ position: "relative", zIndex: 1, width: 24, height: 24, flexShrink: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", borderRadius: "50%", bgcolor: completed ? "#17664f" : "#e2e8f0", color: completed ? "#ffffff" : "#64748b", fontSize: 11, fontWeight: 800, boxShadow: current ? "0 0 0 4px rgba(23,102,79,.12)" : "none" }}>
                      {completed ? "✓" : index + 1}
                    </Box>
                    <Typography variant="caption" sx={{ position: "relative", zIndex: 1, pr: 1, bgcolor: isExceptionStatus ? statusBadge.bg : "#f7fbf9", color: current ? "#17664f" : completed ? "#334155" : "#94a3b8", fontWeight: current ? 800 : 650, lineHeight: 1.25 }}>
                      {step.label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>

            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel id="order-status-select-label" sx={{ fontWeight: 600 }}>
                    Update Order Status
                  </InputLabel>
                  <Select
                    labelId="order-status-select-label"
                    value={initOrder.status || "Placed"}
                    label="Update Order Status"
                    onChange={(e) => handleChangeOrderDetail({ status: e.target.value })}
                    sx={{
                      borderRadius: "8px",
                      bgcolor: "#f8fafc",
                      fontWeight: 700,
                      "& .MuiSelect-select": { py: 1.2 },
                    }}
                  >
                    {isCreatedOrder(detailedOrder?.status) && (
                      <MenuItem value={detailedOrder.status} disabled sx={{ fontWeight: 600, color: "#64748b" }}>
                        Created (Draft Checkout)
                      </MenuItem>
                    )}
                    <MenuItem value="Placed" sx={{ fontWeight: 600, color: "#b45309" }}>
                      ● Placed (Awaiting Shipment)
                    </MenuItem>
                    <MenuItem value="Shipped" sx={{ fontWeight: 600, color: "#6d28d9" }}>
                      ● Shipped
                    </MenuItem>
                    <MenuItem value="Out for delivery" sx={{ fontWeight: 600, color: "#4338ca" }}>
                      ● Out for Delivery
                    </MenuItem>
                    <MenuItem value="Delivered" sx={{ fontWeight: 600, color: "#047857" }}>
                      ● Delivered
                    </MenuItem>
                    <Divider sx={{ my: 1 }} />
                    <MenuItem value="Cancelled by seller" sx={{ fontWeight: 600, color: "#dc2626" }}>
                      ✕ Cancelled by Seller
                    </MenuItem>
                    <MenuItem value="Cancelled by buyer" sx={{ fontWeight: 600, color: "#dc2626" }}>
                      ✕ Cancelled by Buyer
                    </MenuItem>
                    <MenuItem value="Refunded" sx={{ fontWeight: 600, color: "#1d4ed8" }}>
                      ↺ Refunded
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: "8px",
                    bgcolor: statusBadge.bg,
                    border: `1px solid ${statusBadge.border}`,
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                  }}
                >
                  <ScheduleRoundedIcon sx={{ color: statusBadge.color, fontSize: 20 }} />
                  <Box>
                    <Typography variant="caption" sx={{ color: statusBadge.color, fontWeight: 700, display: "block" }}>
                      Current Status: {statusBadge.label}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#64748b", fontSize: "0.72rem" }}>
                      {initOrder.status === "Delivered" && initOrder.deliveredAt
                        ? `Delivered on ${formatDateTimeForAdmin(initOrder.deliveredAt)}`
                        : initOrder.status === "Shipped" && initOrder.shippedAt
                        ? `Shipped on ${formatDateTimeForAdmin(initOrder.shippedAt)}`
                        : `Order recorded on ${formatDateTimeForAdmin(initOrder.createdAt)}`}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>

            {/* Courier Tracking Inputs (Shown when Shipped, Out for delivery, or has courier details) */}
            {(initOrder.status === "Shipped" ||
              initOrder.status === "Out for delivery" ||
              initOrder.status === "Delivered" ||
              Boolean(initOrder.courierDetails?.partner)) && (
              <Box sx={{ mt: 2.5, pt: 2, borderTop: "1px dashed #e2e8f0" }}>
                <Typography variant="caption" sx={{ fontWeight: 800, color: "#475569", textTransform: "uppercase", letterSpacing: "0.05em", mb: 1.5, display: "block" }}>
                  Shipping & Dispatch Details
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Courier Partner"
                      placeholder="e.g. BlueDart, Delhivery"
                      value={initOrder.courierDetails?.partner || ""}
                      onChange={(e) => handleCourierChange("partner", e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LocalShippingOutlinedIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
                          </InputAdornment>
                        ),
                        sx: { borderRadius: "8px", bgcolor: "#f8fafc" },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Tracking ID / AWB"
                      placeholder="e.g. BLU18472910"
                      value={initOrder.courierDetails?.trackingId || ""}
                      onChange={(e) => handleCourierChange("trackingId", e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <ReceiptLongOutlinedIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
                          </InputAdornment>
                        ),
                        endAdornment: initOrder.courierDetails?.trackingId ? (
                          <InputAdornment position="end">
                            <Tooltip title={copiedKey === "trackingId" ? "Copied!" : "Copy"}>
                              <IconButton
                                size="small"
                                onClick={() => handleCopy(initOrder.courierDetails.trackingId, "trackingId")}
                              >
                                <ContentCopyIcon sx={{ fontSize: 15 }} />
                              </IconButton>
                            </Tooltip>
                          </InputAdornment>
                        ) : null,
                        sx: { borderRadius: "8px", bgcolor: "#f8fafc", fontFamily: "ui-monospace, monospace" },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Tracking URL"
                      placeholder="https://track.bluedart.com/..."
                      value={initOrder.courierDetails?.trackingUrl || ""}
                      onChange={(e) => handleCourierChange("trackingUrl", e.target.value)}
                      InputProps={{
                        endAdornment: initOrder.courierDetails?.trackingUrl ? (
                          <InputAdornment position="end">
                            <Tooltip title="Test Tracking Link">
                              <IconButton
                                size="small"
                                component="a"
                                href={initOrder.courierDetails.trackingUrl}
                                target="_blank"
                                rel="noreferrer"
                              >
                                <OpenInNewRoundedIcon sx={{ fontSize: 16, color: "#004d40" }} />
                              </IconButton>
                            </Tooltip>
                          </InputAdornment>
                        ) : null,
                        sx: { borderRadius: "8px", bgcolor: "#f8fafc" },
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>
            )}
          </Paper>

          {/* SECTION B: Ordered Products & Financial Breakdown */}
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: "8px",
              border: "1px solid #e2e8f0",
              bgcolor: "#ffffff",
              boxShadow: "0 2px 10px rgba(0,0,0,0.02)",
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <Inventory2OutlinedIcon sx={{ color: "#004d40", fontSize: 20 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#0f172a" }}>
                Ordered Products ({items.length})
              </Typography>
            </Stack>

            {items.length === 0 ? (
              <Box sx={{ py: 3, textAlign: "center", color: "#94a3b8" }}>
                <Typography variant="body2">No products found in this order payload.</Typography>
              </Box>
            ) : (
              <Stack spacing={1.5} divider={<Divider sx={{ borderColor: "#f1f5f9" }} />}>
                {items.map((item, idx) => {
                  const itemImg = getOrderItemImage(item);
                  const itemTitle = item.title || item.name || "Ayurvedic Formulation";
                  const price = Number(item.sellingPrice || item.listPrice || 0);
                  const qty = Number(item.quantity || 1);
                  const lineTotal = price * qty;

                  return (
                    <Box
                      key={item.id || item.productId || idx}
                      sx={{
                        display: "flex",
                        alignItems: { xs: "flex-start", sm: "center" },
                        justifyContent: "space-between",
                        flexDirection: { xs: "column", sm: "row" },
                        py: 0.8,
                        gap: 2,
                      }}
                    >
                      <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0 }}>
                        <Avatar
                          src={itemImg}
                          alt={itemTitle}
                          variant="rounded"
                          sx={{
                            width: 48,
                            height: 48,
                            borderRadius: "8px",
                            border: "1px solid #e2e8f0",
                            bgcolor: "#f8fafc",
                          }}
                        >
                          <Inventory2OutlinedIcon sx={{ color: "#94a3b8" }} />
                        </Avatar>

                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 700,
                              color: "#1e293b",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {itemTitle}
                          </Typography>

                          <Typography variant="caption" sx={{ color: "#64748b", display: "block" }}>
                            {formatMoney(price)} × {qty} {qty === 1 ? "unit" : "units"}
                            {item.size || item.volume || item.weight ? ` • ${item.size || item.volume || item.weight}` : ""}
                          </Typography>
                        </Box>
                      </Stack>

                      <Box sx={{ textAlign: { xs: "left", sm: "right" }, flexShrink: 0, pl: { xs: 8, sm: 0 } }}>
                        <Typography variant="body2" sx={{ fontWeight: 800, color: "#0f172a" }}>
                          {formatMoney(lineTotal)}
                        </Typography>
                        <Chip
                          label={`Qty: ${qty}`}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: "0.68rem",
                            fontWeight: 700,
                            bgcolor: "#f1f5f9",
                            color: "#475569",
                            mt: 0.3,
                          }}
                        />
                      </Box>
                    </Box>
                  );
                })}
              </Stack>
            )}

            {/* Financial Summary Breakdown */}
            <Box sx={{ mt: 3, pt: 2, borderTop: "1px solid #f1f5f9" }}>
              <Stack spacing={1} sx={{ maxWidth: 360, ml: "auto" }}>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600 }}>
                    Items Subtotal
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: "#1e293b" }}>
                    {formatMoney(itemsSubtotal)}
                  </Typography>
                </Box>

                {Boolean(initOrder.deliveryCharges || initOrder.shippingCost) && (
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600 }}>
                      Delivery / Shipping
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: "#1e293b" }}>
                      {formatMoney(initOrder.deliveryCharges || initOrder.shippingCost)}
                    </Typography>
                  </Box>
                )}

                {Boolean(initOrder.codCharges) && (
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600 }}>
                      Cash on Delivery Fee
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: "#1e293b" }}>
                      {formatMoney(initOrder.codCharges)}
                    </Typography>
                  </Box>
                )}

                {Boolean(initOrder.couponApplied) && (
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="caption" sx={{ color: "#047857", fontWeight: 700 }}>
                      Coupon Discount ({couponCode})
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: "#047857" }}>
                      - {formatMoney(couponDiscount)}
                    </Typography>
                  </Box>
                )}

                <Divider sx={{ my: 0.5, borderColor: "#e2e8f0" }} />

                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#0f172a" }}>
                    Total Order Value
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 900, color: "#004d40", fontSize: "1.15rem" }}>
                    {formatMoney(grandTotal)}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          </Paper>

          {/* SECTION C: 2-Column Details (Customer & Shipping + Payment) */}
          <Grid container spacing={2.5}>
            {/* Customer & Shipping Address */}
            <Grid item xs={12} md={7}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: "8px",
                  border: "1px solid #e2e8f0",
                  bgcolor: "#ffffff",
                  height: "100%",
                  boxShadow: "0 2px 10px rgba(0,0,0,0.02)",
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                  <PersonOutlineRoundedIcon sx={{ color: "#004d40", fontSize: 20 }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#0f172a" }}>
                    Customer & Shipping Details
                  </Typography>
                </Stack>

                <Stack spacing={1.5}>
                  {/* Customer Account Info */}
                  <Box sx={{ p: 1.5, borderRadius: "8px", bgcolor: "#f8fafc", border: "1px solid #f1f5f9" }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: "#64748b", textTransform: "uppercase", fontSize: "0.68rem" }}>
                      Account Holder
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 800, color: "#1e293b", mt: 0.3 }}>
                      {initOrder.user?.name || initOrder.address?.name || "Customer"}
                    </Typography>

                    <Stack direction="row" spacing={2} sx={{ mt: 0.5 }} flexWrap="wrap">
                      {initOrder.user?.email && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <MailOutlineRoundedIcon sx={{ fontSize: 14, color: "#94a3b8" }} />
                          <Typography
                            component="a"
                            href={`mailto:${initOrder.user.email}`}
                            variant="caption"
                            sx={{ color: "primary.main", textDecoration: "none", fontWeight: 600 }}
                          >
                            {initOrder.user.email}
                          </Typography>
                        </Box>
                      )}

                      {initOrder.user?.id && (
                        <Tooltip title="User ID">
                          <Typography
                            variant="caption"
                            sx={{ color: "#94a3b8", fontFamily: "ui-monospace, monospace", fontSize: "0.7rem" }}
                          >
                            #{initOrder.user.id}
                          </Typography>
                        </Tooltip>
                      )}
                    </Stack>
                  </Box>

                  {/* Shipping Address */}
                  <Box sx={{ p: 1.5, borderRadius: "8px", bgcolor: "#f8fafc", border: "1px solid #f1f5f9" }}>
                    <Stack direction="row" spacing={0.8} alignItems="center" sx={{ mb: 0.5 }}>
                      <LocationOnOutlinedIcon sx={{ fontSize: 16, color: "#047857" }} />
                      <Typography variant="caption" sx={{ fontWeight: 700, color: "#64748b", textTransform: "uppercase", fontSize: "0.68rem" }}>
                        Delivery Address
                      </Typography>
                    </Stack>

                    <Typography variant="body2" sx={{ fontWeight: 700, color: "#1e293b" }}>
                      {initOrder.address?.name || "Recipient"}
                    </Typography>

                    <Typography variant="caption" sx={{ color: "#475569", lineHeight: 1.5, display: "block", mt: 0.3 }}>
                      {initOrder.address?.address}
                      {initOrder.address?.landmark ? `, Landmark: ${initOrder.address.landmark}` : ""}
                      <br />
                      {initOrder.address?.city}, {initOrder.address?.state} —{" "}
                      <strong>{initOrder.address?.pincode}</strong>
                    </Typography>

                    {initOrder.address?.mobile && (
                      <Stack spacing={0.5} sx={{ mt: 1 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <PhoneOutlinedIcon sx={{ fontSize: 14, color: "#94a3b8" }} />
                          <Typography
                            component="a"
                            href={`tel:${initOrder.address.mobile}`}
                            variant="caption"
                            sx={{ color: "#0f172a", textDecoration: "none", fontWeight: 700 }}
                          >
                            {initOrder.address.mobile}
                          </Typography>
                        </Stack>
                        {initOrder.address?.alternateMobile && (
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ pl: 2.75, minWidth: 0 }}>
                            <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600, whiteSpace: "nowrap", flexShrink: 0 }}>
                              Alt contact:
                            </Typography>
                            <Typography
                              component="a"
                              href={`tel:${initOrder.address.alternateMobile}`}
                              variant="caption"
                              sx={{ color: "#0f172a", textDecoration: "none", fontWeight: 700, minWidth: 0, overflowWrap: "anywhere" }}
                            >
                              {initOrder.address.alternateMobile}
                            </Typography>
                          </Stack>
                        )}
                      </Stack>
                    )}
                  </Box>
                </Stack>
              </Paper>
            </Grid>

            {/* Payment Details */}
            <Grid item xs={12} md={5}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: "8px",
                  border: "1px solid #e2e8f0",
                  bgcolor: "#ffffff",
                  height: "100%",
                  boxShadow: "0 2px 10px rgba(0,0,0,0.02)",
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                  <PaymentOutlinedIcon sx={{ color: "#004d40", fontSize: 20 }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#0f172a" }}>
                    Payment Information
                  </Typography>
                </Stack>

                <Stack spacing={1.5}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600 }}>
                      Method
                    </Typography>
                    <Chip
                      label={
                        initOrder.payment?.method?.toLowerCase() === "cod"
                          ? "Cash on Delivery"
                          : initOrder.payment?.method || "Online"
                      }
                      size="small"
                      sx={{
                        fontWeight: 800,
                        fontSize: "0.72rem",
                        bgcolor: initOrder.payment?.method?.toLowerCase() === "cod" ? "#fffbeb" : "#f0fdf4",
                        color: initOrder.payment?.method?.toLowerCase() === "cod" ? "#b45309" : "#166534",
                        border: "1px solid",
                        borderColor: initOrder.payment?.method?.toLowerCase() === "cod" ? "#fde68a" : "#bbf7d0",
                      }}
                    />
                  </Box>

                  <Divider sx={{ borderColor: "#f1f5f9" }} />

                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600 }}>
                      Payment Status
                    </Typography>
                    <Chip
                      label={paymentBadge.label}
                      size="small"
                      sx={{
                        fontWeight: 800,
                        fontSize: "0.72rem",
                        bgcolor: paymentBadge.bg,
                        color: paymentBadge.color,
                        border: `1px solid ${paymentBadge.border}`,
                      }}
                    />
                  </Box>

                  {initOrder.payment?.payment_id && (
                    <>
                      <Divider sx={{ borderColor: "#f1f5f9" }} />
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                        <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600 }}>
                          Transaction ID
                        </Typography>
                        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0, maxWidth: "68%" }}>
                          <Typography
                            variant="caption"
                            sx={{
                              fontFamily: "ui-monospace, monospace",
                              fontWeight: 700,
                              color: "#1e293b",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {initOrder.payment.payment_id}
                          </Typography>
                          <Tooltip title={copiedKey === "paymentId" ? "Copied!" : "Copy"}>
                            <IconButton
                              size="small"
                              onClick={() => handleCopy(initOrder.payment.payment_id, "paymentId")}
                              aria-label="Copy transaction ID"
                              sx={{ p: 0.3 }}
                            >
                              <ContentCopyIcon sx={{ fontSize: 13 }} />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </Box>
                    </>
                  )}

                  {initOrder.payment?.attempted_at && (
                    <>
                      <Divider sx={{ borderColor: "#f1f5f9" }} />
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600 }}>
                          Attempted At
                        </Typography>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: "#1e293b" }}>
                          {formatDateTimeForAdmin(initOrder.payment.attempted_at)}
                        </Typography>
                      </Box>
                    </>
                  )}
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Stack>
      </Box>

      {/* 3. Modal Bottom Action Bar / Footer */}
      <Box
        sx={{
          px: { xs: 2, sm: 3.5 },
          py: { xs: 1.5, sm: 2 },
          borderTop: "1px solid #f1f5f9",
          bgcolor: "#ffffff",
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          gap: 1.5,
          flexShrink: 0,
        }}
      >
        {isCreatedOrder(initOrder.status) ? (
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteOutlineRoundedIcon />}
            onClick={() => setDeleteDialogOpen(true)}
            disabled={isDeleting}
            sx={{ borderRadius: "8px", fontWeight: 700, textTransform: "none", whiteSpace: "nowrap" }}
          >
            Delete Order
          </Button>
        ) : (
          <Box sx={{ display: { xs: "none", sm: "block" } }}>
            <Typography variant="caption" sx={{ color: hasChanges ? "#17664f" : "#64748b", fontWeight: hasChanges ? 700 : 500, display: "block" }}>
              {hasChanges ? "Unsaved order updates are ready to save." : "No unsaved changes."}
            </Typography>
            {initOrder.status !== detailedOrder?.status && (
              <Typography variant="caption" sx={{ color: "#64748b", fontSize: "0.68rem" }}>
                Saving the new status will notify the customer.
              </Typography>
            )}
          </Box>
        )}

        <Stack direction="row" spacing={1.5} sx={{ ml: { sm: "auto" }, width: { xs: "100%", sm: "auto" } }}>
          <Button
            variant="outlined"
            onClick={hideModal}
            disabled={isUpdating}
            sx={{
              borderRadius: "8px",
              fontWeight: 700,
              textTransform: "none",
              borderColor: "#cbd5e1",
              color: "#475569",
              px: 2.5,
              py: 0.9,
              fontSize: "0.875rem",
              flex: { xs: 1, sm: "initial" },
              "&:hover": { borderColor: "#94a3b8", bgcolor: "#f8fafc" },
            }}
          >
            Close
          </Button>

          <Button
            variant="contained"
            onClick={onClickUpdate}
            disabled={isUpdating || !hasChanges}
            startIcon={
              isUpdating ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <SaveRoundedIcon />
              )
            }
            sx={{
              borderRadius: "8px",
              fontWeight: 700,
              textTransform: "none",
              bgcolor: "#004d40",
              px: 3,
              py: 0.9,
              fontSize: "0.875rem",
              flex: { xs: 1.4, sm: "initial" },
              boxShadow: "0 4px 12px rgba(0, 77, 64, 0.2)",
              "&:hover": { bgcolor: "#00382e" },
              "&.Mui-disabled": { bgcolor: "#e2e8f0", color: "#94a3b8", boxShadow: "none" },
            }}
          >
            {isUpdating ? "Saving..." : "Save Changes"}
          </Button>
        </Stack>
      </Box>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => { if (!isDeleting) setDeleteDialogOpen(false); }}
        aria-labelledby="delete-order-dialog-title"
      >
        <DialogTitle id="delete-order-dialog-title" sx={{ fontWeight: 800 }}>
          Delete draft order?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Order #{initOrder.id} is still in CREATED status. Deleting it is permanent and cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            disabled={isDeleting}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Keep Order
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteOrder}
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineRoundedIcon />}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            {isDeleting ? "Deleting..." : "Delete Order"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ViewOrder;
